/**
 * The route table — a **shared file** under the §6.1 discipline.
 *
 * A packet that adds a surface adds **exactly one line**, in sorted position,
 * and changes nothing else in this file. Never reformat, never re-order, never
 * change quote style or trailing commas: those turn a one-line addition into a
 * whole-file conflict for every packet editing it in the same week. If two
 * additions conflict in git, the resolution is always "keep both, re-sort".
 *
 * `fullViewport` entries render **over** the shell rather than inside a
 * destination root, because the live workout owns the whole viewport and must
 * not compete with the dock. They are still reached through the destination
 * they belong to, which is why they carry one.
 *
 * There is no `<Routes>` switch behind this table. F-NAV-03 keeps all four
 * destination roots mounted simultaneously, and a router that picked one of
 * them would unmount the other three — the parity regression the spec names by
 * name. `path` is what the URL is written *to* when a destination is selected;
 * it is never read back (F-NAV-23).
 */

import type { ComponentType } from "react";
import type { DestinationId } from "./destinations";

export interface RouteEntry {
  readonly path: string;
  readonly destination: DestinationId;
  readonly load: () => Promise<{ readonly default: ComponentType }>;
  readonly fullViewport?: boolean;
}

export const ROUTE_TABLE: readonly RouteEntry[] = [
  // Sorted by `path`, ASCII ascending. One line per entry. Do not reformat.
  { path: "/",             destination: "train",    load: () => import("../features/train/TrainRoute") },
  { path: "/build/*",      destination: "build",    load: () => import("../features/build/BuildRoute") },
  { path: "/journal/*",    destination: "journal",  load: () => import("../features/journal/JournalRoute") },
  { path: "/journal/compose", destination: "journal", load: () => import("../features/journal/composer/JournalComposerRoute"), fullViewport: true },
  { path: "/journal/entry", destination: "journal", load: () => import("../features/journal/reader/JournalReaderRoute"), fullViewport: true },
  { path: "/journal/goals", destination: "journal", load: () => import("../features/journal/goals/GoalsRoute"), fullViewport: true },
  { path: "/settings/*",   destination: "settings", load: () => import("../features/settings/SettingsRoute") },
  { path: "/settings/drills", destination: "settings", load: () => import("../features/build/drills/DrillLibraryRoute"), fullViewport: true },
  { path: "/settings/history", destination: "settings", load: () => import("../features/history/HistoryRoute"), fullViewport: true },
  { path: "/train/live",   destination: "train",    load: () => import("../features/train/live/LiveWorkoutRoute"), fullViewport: true },
  { path: "/train/summary", destination: "train",   load: () => import("../features/summary/SummaryRoute"), fullViewport: true },
];

/**
 * The one entry that *is* a destination root. A destination may own several
 * routes, but exactly one of them is the persistently mounted surface; the
 * others are full-viewport presentations over it.
 */
export function destinationRootEntry(destination: DestinationId): RouteEntry {
  const found = ROUTE_TABLE.find(
    (entry) => entry.destination === destination && entry.fullViewport !== true,
  );
  if (found === undefined) {
    throw new Error(
      `No root route is declared for the "${destination}" destination. Every destination needs ` +
        "exactly one non-fullViewport entry in ROUTE_TABLE.",
    );
  }
  return found;
}

export function fullViewportEntries(): readonly RouteEntry[] {
  return ROUTE_TABLE.filter((entry) => entry.fullViewport === true);
}

/**
 * The full-viewport entry registered at `path`, or a thrown error naming the
 * gap.
 *
 * A caller that opens one specific surface must **name** it and look it up
 * here. It must never take "the first `fullViewport` row" — the table is sorted
 * by path so that a packet's addition stays a one-line diff, and that ordering
 * is merge hygiene, not meaning. Deriving a surface from position is how the
 * Ringside action came to open `/settings/drills`: the drill library was added
 * in sorted position, ahead of `/train/live`, and nothing failed until a test
 * read the address back.
 *
 * Throwing rather than returning `undefined` is the point. Callers resolve at
 * module scope, so a surface that was renamed out from under one fails at
 * import with the path it wanted, instead of opening the wrong screen.
 */
export function fullViewportEntry(path: string): RouteEntry {
  const found = ROUTE_TABLE.find((entry) => entry.path === path && entry.fullViewport === true);
  if (found === undefined) {
    throw new Error(
      `No full-viewport route is declared at "${path}". A surface opened by path must have a ` +
        "matching ROUTE_TABLE entry with `fullViewport: true`.",
    );
  }
  return found;
}
